import { timeAndLeaveApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import { ApiError } from '../identity/types';
import type { TimeAndLeaveGateway } from './TimeAndLeaveGateway';
import type {
  AttendanceEntryResponse, EmployeeHolidayAssignmentResponse, HolidayResponse, HoursForPeriodResponse,
  LeaveBalanceResponse, LeavePlanEnrollmentResponse, LeavePlanResponse, LeaveRequestResponse, CreateLeavePlanInput,
  LeavePlanSetupRequest, LeavePlanSetupResponse, LeavePlanSetupSettings, LeavePlanSetupTypeResponse,
  CreateEscoHrMappingInput, EscoHrConnectionCreatedResponse, EscoHrEventResponse, EscoHrMappingResponse, EscoHrSigningSecretResponse,
  EscoHrStatusResponse, EscoHrExceptionResponse, EscoHrLiveAttendanceResponse, EscoHrProcessResult, EscoHrPunchResponse, EscoHrSyntheticResult,
  EscoHrExpenseClaimResponse, EscoHrHolidayCalendarResponse, EscoHrHolidayRequestResponse, EscoHrLeaveRequestResponse, EscoHrWorkforceSummaryResponse,
} from './types';

export class HttpTimeAndLeaveGateway implements TimeAndLeaveGateway {
  private readonly request = createJsonClient(timeAndLeaveApiBaseUrl);

  listAttendanceEntries(employeeId: string, signal?: AbortSignal): Promise<AttendanceEntryResponse[]> {
    return this.request(`/api/employees/${employeeId}/attendance-entries`, undefined, signal);
  }

  createAttendanceEntry(employeeId: string, input: { workDate: string; workedHours: number; overtimeHours: number; notes: string | null }): Promise<AttendanceEntryResponse> {
    return this.request(`/api/employees/${employeeId}/attendance-entries`, { method: 'POST', body: JSON.stringify(input) });
  }

  updateAttendanceEntry(id: string, input: { workedHours: number; overtimeHours: number; notes: string | null }): Promise<AttendanceEntryResponse> {
    return this.request(`/api/attendance-entries/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  approveAttendanceEntry(id: string): Promise<AttendanceEntryResponse> {
    return this.request(`/api/attendance-entries/${id}/approve`, { method: 'POST' });
  }

  rejectAttendanceEntry(id: string, rejectionReason: string): Promise<AttendanceEntryResponse> {
    return this.request(`/api/attendance-entries/${id}/reject`, { method: 'POST', body: JSON.stringify({ rejectionReason }) });
  }

  reopenAttendanceEntry(id: string): Promise<AttendanceEntryResponse> {
    return this.request(`/api/attendance-entries/${id}/reopen`, { method: 'POST' });
  }

  getHoursForPeriod(employeeId: string, start: string, end: string): Promise<HoursForPeriodResponse> {
    return this.request(`/api/employees/${employeeId}/attendance-entries/hours-for-period?start=${start}&end=${end}`);
  }

  listLeavePlans(signal?: AbortSignal): Promise<LeavePlanResponse[]> {
    return this.request('/api/leave-plans', undefined, signal);
  }

  createLeavePlan(input: CreateLeavePlanInput): Promise<LeavePlanResponse> {
    return this.request('/api/leave-plans', { method: 'POST', body: JSON.stringify({ ...input, accrualTiers: [] }) });
  }

  listEnrollments(employeeId: string, signal?: AbortSignal): Promise<LeavePlanEnrollmentResponse[]> {
    return this.request(`/api/employees/${employeeId}/leave-plan-enrollments`, undefined, signal);
  }

  listLeavePlanSetups(employeeId: string, signal?: AbortSignal): Promise<LeavePlanSetupResponse[]> {
    return this.request(`/api/employees/${employeeId}/leave-plan-setups`, undefined, signal);
  }

  createLeavePlanSetup(employeeId: string, input: LeavePlanSetupRequest): Promise<LeavePlanSetupResponse> {
    return this.request(`/api/employees/${employeeId}/leave-plan-setups`, { method: 'POST', body: JSON.stringify(input) });
  }

  updateLeavePlanSetup(id: string, input: LeavePlanSetupSettings): Promise<LeavePlanSetupResponse> {
    return this.request(`/api/leave-plan-setups/${id}`, { method: 'PUT', body: JSON.stringify(input) });
  }

  endLeavePlanSetup(id: string): Promise<LeavePlanSetupResponse> {
    return this.request(`/api/leave-plan-setups/${id}/end`, { method: 'POST' });
  }

  listLeavePlanSetupTypes(signal?: AbortSignal): Promise<LeavePlanSetupTypeResponse[]> {
    return this.request('/api/leave-plan-setup-types', undefined, signal);
  }

  enrollEmployee(employeeId: string, leavePlanId: string): Promise<LeavePlanEnrollmentResponse> {
    return this.request(`/api/employees/${employeeId}/leave-plan-enrollments`, { method: 'POST', body: JSON.stringify({ leavePlanId }) });
  }

  async getLeaveBalance(employeeId: string, leavePlanId: string, signal?: AbortSignal): Promise<LeaveBalanceResponse | null> {
    try {
      return await this.request(`/api/employees/${employeeId}/leave-balances/${leavePlanId}`, undefined, signal);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  accrueForPeriod(employeeId: string, leavePlanId: string, input: { periodStartDate: string; periodEndDate: string; payPeriodsPerYear: number }): Promise<LeaveBalanceResponse> {
    return this.request(`/api/employees/${employeeId}/leave-balances/${leavePlanId}/accrue`, { method: 'POST', body: JSON.stringify(input) });
  }

  listLeaveRequests(employeeId: string, signal?: AbortSignal): Promise<LeaveRequestResponse[]> {
    return this.request(`/api/employees/${employeeId}/leave-requests`, undefined, signal);
  }

  createLeaveRequest(employeeId: string, input: { leavePlanId: string; reason: string | null; days: { leaveDate: string; hours: number }[] }): Promise<LeaveRequestResponse> {
    return this.request(`/api/employees/${employeeId}/leave-requests`, { method: 'POST', body: JSON.stringify(input) });
  }

  approveLeaveRequest(id: string): Promise<LeaveRequestResponse> {
    return this.request(`/api/leave-requests/${id}/approve`, { method: 'POST' });
  }

  rejectLeaveRequest(id: string, rejectionReason: string): Promise<LeaveRequestResponse> {
    return this.request(`/api/leave-requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ rejectionReason }) });
  }

  cancelLeaveRequest(id: string): Promise<LeaveRequestResponse> {
    return this.request(`/api/leave-requests/${id}/cancel`, { method: 'POST' });
  }

  createSpecialGrant(employeeId: string, input: { leavePlanId: string; grantDates: string[]; hoursPerDay: number; reason: string }): Promise<LeaveRequestResponse> {
    return this.request(`/api/employees/${employeeId}/leave-requests/special-grant`, { method: 'POST', body: JSON.stringify(input) });
  }

  listHolidays(signal?: AbortSignal): Promise<HolidayResponse[]> {
    return this.request('/api/holidays', undefined, signal);
  }

  createHoliday(input: { name: string; holidayDate: string; isFixed: boolean; defaultWorkHourCredit: number | null; defaultLeaveHourCredit: number | null }): Promise<HolidayResponse> {
    return this.request('/api/holidays', { method: 'POST', body: JSON.stringify(input) });
  }

  listEmployeeHolidayAssignments(employeeId: string, signal?: AbortSignal): Promise<EmployeeHolidayAssignmentResponse[]> {
    return this.request(`/api/employees/${employeeId}/holiday-assignments`, undefined, signal);
  }

  assignHoliday(employeeId: string, holidayDefinitionId: string): Promise<EmployeeHolidayAssignmentResponse> {
    return this.request(`/api/employees/${employeeId}/holiday-assignments`, { method: 'POST', body: JSON.stringify({ holidayDefinitionId }) });
  }

  getEscoHrStatus(signal?: AbortSignal): Promise<EscoHrStatusResponse> {
    return this.request('/api/time-attendance/escohr/status', undefined, signal);
  }

  connectEscoHr(): Promise<EscoHrConnectionCreatedResponse> {
    return this.request('/api/time-attendance/escohr/connection', { method: 'POST' });
  }

  disconnectEscoHr(): Promise<void> {
    return this.request('/api/time-attendance/escohr/connection', { method: 'DELETE' });
  }

  issueEscoHrSigningSecret(): Promise<EscoHrSigningSecretResponse> {
    return this.request('/api/time-attendance/escohr/connection/signing-secret', { method: 'POST' });
  }

  listEscoHrEvents(take = 50, signal?: AbortSignal): Promise<EscoHrEventResponse[]> {
    return this.request(`/api/time-attendance/escohr/events?take=${take}`, undefined, signal);
  }

  listEscoHrMappings(signal?: AbortSignal): Promise<EscoHrMappingResponse[]> {
    return this.request('/api/time-attendance/escohr/mappings', undefined, signal);
  }

  createEscoHrMapping(input: CreateEscoHrMappingInput): Promise<EscoHrMappingResponse> {
    return this.request('/api/time-attendance/escohr/mappings', { method: 'POST', body: JSON.stringify(input) });
  }

  removeEscoHrMapping(id: string): Promise<void> {
    return this.request(`/api/time-attendance/escohr/mappings/${id}`, { method: 'DELETE' });
  }

  listEscoHrPunches(take = 50, signal?: AbortSignal): Promise<EscoHrPunchResponse[]> {
    return this.request(`/api/time-attendance/escohr/punches?take=${take}`, undefined, signal);
  }

  getEscoHrLiveAttendance(signal?: AbortSignal): Promise<EscoHrLiveAttendanceResponse[]> {
    return this.request('/api/time-attendance/escohr/live', undefined, signal);
  }

  listEscoHrExceptions(signal?: AbortSignal): Promise<EscoHrExceptionResponse[]> {
    return this.request('/api/time-attendance/escohr/exceptions', undefined, signal);
  }

  processEscoHrEvents(): Promise<EscoHrProcessResult> {
    return this.request('/api/time-attendance/escohr/events/process', { method: 'POST' });
  }

  generateEscoHrSyntheticData(): Promise<EscoHrSyntheticResult> {
    return this.request('/api/time-attendance/escohr/synthetic-events', { method: 'POST' });
  }

  getEscoHrTimeOffSummary(signal?: AbortSignal): Promise<EscoHrWorkforceSummaryResponse> {
    return this.request('/api/time-attendance/escohr/time-off/summary', undefined, signal);
  }

  listEscoHrLeaveRequests(signal?: AbortSignal): Promise<EscoHrLeaveRequestResponse[]> {
    return this.request('/api/time-attendance/escohr/time-off/requests?take=200', undefined, signal);
  }

  getEscoHrHolidaysSummary(signal?: AbortSignal): Promise<EscoHrWorkforceSummaryResponse> {
    return this.request('/api/time-attendance/escohr/holidays/summary', undefined, signal);
  }

  listEscoHrHolidayCalendar(signal?: AbortSignal): Promise<EscoHrHolidayCalendarResponse[]> {
    return this.request('/api/time-attendance/escohr/holidays/calendar?take=400', undefined, signal);
  }

  listEscoHrHolidayRequests(signal?: AbortSignal): Promise<EscoHrHolidayRequestResponse[]> {
    return this.request('/api/time-attendance/escohr/holidays/requests?take=200', undefined, signal);
  }

  getEscoHrExpensesSummary(signal?: AbortSignal): Promise<EscoHrWorkforceSummaryResponse> {
    return this.request('/api/time-attendance/escohr/expenses/summary', undefined, signal);
  }

  listEscoHrExpenseClaims(signal?: AbortSignal): Promise<EscoHrExpenseClaimResponse[]> {
    return this.request('/api/time-attendance/escohr/expenses/claims?take=200', undefined, signal);
  }
}
